import { useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  projectFileQuery,
  projectFileVersionsQuery,
  type ProjectFileVersion,
} from '@/entities/project-file';
import { projectFolderQuery } from '@/entities/project-folder';
import { EmptyState } from '@/shared/ui/EmptyState';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBackButton,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import type { ProjectDetailContext } from '../Folders/folders.types';
import { useAssetApprovalChain } from '@/entities/asset-approval-chain';
import { useProjectCapabilities } from '@/entities/user';
import { useApprovalChainsEnabled } from '@/shared/hooks/useApprovalChainsEnabled';
import { useAssetApproval } from '../AssetDetail/hooks/useAssetApproval';
import { AssetApprovalPrimary } from '../AssetDetail/components/AssetApprovalPrimary';
import { AssetComparePane } from './AssetComparePane';
import { CompareViewProvider } from './CompareViewContext';
import { SyncPlayToggleButton } from './SyncPlayToggleButton';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@monksflow/monks-ui';

export function AssetCompareView() {
  const { t } = useTranslation('projects');

  const { folderId, fileId } = useParams<{ folderId: string; fileId: string }>();
  const { project } = useOutletContext<ProjectDetailContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const leftId = searchParams.get('left');
  const rightId = searchParams.get('right');

  const { data: file, isLoading: isFileLoading } = useQuery(
    projectFileQuery(project.platformProjectId, fileId),
  );

  const { data: folder } = useQuery(projectFolderQuery(project.platformProjectId, folderId));

  const { data: versionsData, isLoading: areVersionsLoading } = useQuery(
    projectFileVersionsQuery(project.platformProjectId, fileId),
  );

  // Stabilize the array identity — passed to two panes as `allVersions`, so
  // a fresh `[]` every render would defeat any downstream memo.
  const versions = useMemo<ProjectFileVersion[]>(() => versionsData ?? [], [versionsData]);

  // Latest = newest by createdAt (same convention as the single-asset view's
  // useSelectedVersion hook). Drives the approve-button visibility per pane.
  const latestVersionId = useMemo<string | null>(() => {
    if (!versions.length) return null;
    const sorted = [...versions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return sorted[0].id;
  }, [versions]);

  const { data: assetApprovalChain, isLoading: isLoadingApprovalChain } = useAssetApprovalChain(
    project.platformProjectId,
    fileId,
  );
  const projects = useProjectCapabilities();
  const approvalChainsEnabled = useApprovalChainsEnabled();
  const approval = useAssetApproval({
    projectId: project.platformProjectId,
    folderId: folderId ?? '',
    fileId: fileId ?? '',
    fileApproved: file?.status === 'approved',
    canApproveAssets: projects.canApproveAssets,
  });

  // No `rail`: compare has no approval sidebar to open, so a chained asset reads its progress
  // here and acts on it back on the asset page.
  const approvalControl = (
    <AssetApprovalPrimary
      approval={approval}
      chain={approvalChainsEnabled ? assetApprovalChain : null}
      isLoadingChain={approvalChainsEnabled && isLoadingApprovalChain}
    />
  );

  const handleBackClick = useCallback(() => {
    const target =
      folderId && fileId
        ? ROUTES.PROJECT_FILE_DETAIL(project.platformProjectId, folderId, fileId)
        : ROUTES.PROJECTS;
    void navigate(target);
  }, [navigate, project.platformProjectId, folderId, fileId]);

  const handleSelectVersion = useCallback(
    (side: 'left' | 'right', versionId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(side, versionId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const isLoading = isFileLoading || areVersionsLoading;

  return (
    <CompareViewProvider>
      <div className="flex h-full min-h-0 flex-col">
        <PageHeader focus>
          <PageHeaderIdentity>
            <PageHeaderBackButton onClick={handleBackClick} />
            <PageHeaderBreadcrumb maxItems={4}>
              <PageHeaderBreadcrumbLink href={ROUTES.PROJECT_FOLDERS(project.platformProjectId)}>
                {t('projectDetail.assetCompareView.folders')}
              </PageHeaderBreadcrumbLink>
              {(folder?.breadcrumbs ?? []).map((ancestor) => (
                <PageHeaderBreadcrumbLink
                  key={ancestor.id}
                  href={ROUTES.PROJECT_FOLDER_DETAIL(project.platformProjectId, ancestor.id)}
                >
                  {ancestor.name}
                </PageHeaderBreadcrumbLink>
              ))}
              {folderId && (
                <PageHeaderBreadcrumbLink
                  href={ROUTES.PROJECT_FOLDER_DETAIL(project.platformProjectId, folderId)}
                >
                  {folder?.name ?? '...'}
                </PageHeaderBreadcrumbLink>
              )}
              {folderId && fileId && (
                <PageHeaderBreadcrumbLink
                  href={ROUTES.PROJECT_FILE_DETAIL(project.platformProjectId, folderId, fileId)}
                >
                  {file?.fileName ?? '...'}
                </PageHeaderBreadcrumbLink>
              )}
              <PageHeaderBreadcrumbPage>
                {t('projectDetail.assetCompareView.compareVersions')}
              </PageHeaderBreadcrumbPage>
            </PageHeaderBreadcrumb>
          </PageHeaderIdentity>
          <PageHeaderActions>
            <SyncPlayToggleButton />
          </PageHeaderActions>
        </PageHeader>
        <div className="flex flex-1 min-h-0 flex-col gap-3 mt-4">
          <CompareContent
            isLoading={isLoading}
            file={file}
            versions={versions}
            leftId={leftId}
            rightId={rightId}
            latestVersionId={latestVersionId}
            projectId={project.platformProjectId}
            folderId={folderId ?? ''}
            onSelectVersion={handleSelectVersion}
            approvalControl={approvalControl}
          />
        </div>
      </div>
    </CompareViewProvider>
  );
}

interface CompareContentProps {
  isLoading: boolean;
  file: ProjectFileResponseDto | undefined;
  versions: ProjectFileVersion[];
  leftId: string | null;
  rightId: string | null;
  latestVersionId: string | null;
  projectId: string;
  folderId: string;
  onSelectVersion: (side: 'left' | 'right', versionId: string) => void;
  approvalControl: ReactNode;
}

function CompareContent({
  isLoading,
  file,
  versions,
  leftId,
  rightId,
  latestVersionId,
  projectId,
  folderId,
  onSelectVersion,
  approvalControl,
}: CompareContentProps) {
  const { t } = useTranslation('projects');

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" className="text-muted-foreground" />
      </div>
    );
  }

  if (!leftId || !rightId || leftId === rightId) {
    return (
      <EmptyState
        title={t('projectDetail.assetCompareView.pickTwoDifferentVersionsTo')}
        description={t('projectDetail.assetCompareView.openAFileAndUse')}
      />
    );
  }

  const leftVersion = versions.find((v) => v.id === leftId);
  const rightVersion = versions.find((v) => v.id === rightId);

  if (!file || !leftVersion || !rightVersion) {
    return (
      <EmptyState
        title={t('projectDetail.assetCompareView.versionsNotFound')}
        description={t('projectDetail.assetCompareView.oneOfTheSelectedVersions')}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 gap-4">
      {/* Keying on the version id forces a fresh per-pane provider tree when
          the user swaps versions, so zoom / page / playback state from the
          previous version never carries over. */}
      <AssetComparePane
        key={`left-${leftVersion.id}`}
        side="left"
        file={file}
        version={leftVersion}
        allVersions={versions}
        otherVersionId={rightVersion.id}
        isLatest={leftVersion.id === latestVersionId}
        projectId={projectId}
        folderId={folderId}
        onSelectVersion={(id) => onSelectVersion('left', id)}
        approvalControl={approvalControl}
      />
      <AssetComparePane
        key={`right-${rightVersion.id}`}
        side="right"
        file={file}
        version={rightVersion}
        allVersions={versions}
        otherVersionId={leftVersion.id}
        isLatest={rightVersion.id === latestVersionId}
        projectId={projectId}
        folderId={folderId}
        onSelectVersion={(id) => onSelectVersion('right', id)}
        approvalControl={approvalControl}
      />
    </div>
  );
}
